import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, Briefcase, Clock, CheckCircle } from 'lucide-react';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('shansoats_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansoats/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  if (!analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="w-8 h-8 text-yellow-400" />
        <div>
          <h2 className="text-3xl font-bold text-white">Smart Analytics</h2>
          <p className="text-gray-400">Real-time recruitment insights</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 rounded-xl p-6">
          <Briefcase className="w-8 h-8 text-blue-400 mb-3" />
          <p className="text-sm text-blue-400 mb-1">Total Jobs Posted</p>
          <p className="text-3xl font-bold text-white">{analytics.total_jobs || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 border border-cyan-500/30 rounded-xl p-6">
          <Users className="w-8 h-8 text-cyan-400 mb-3" />
          <p className="text-sm text-cyan-400 mb-1">Total Candidates</p>
          <p className="text-3xl font-bold text-white">{analytics.total_candidates || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-500/30 rounded-xl p-6">
          <CheckCircle className="w-8 h-8 text-green-400 mb-3" />
          <p className="text-sm text-green-400 mb-1">Successful Hires</p>
          <p className="text-3xl font-bold text-white">{analytics.hired_count || 0}</p>
        </div>
      </div>

      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Pipeline Distribution</h3>
        <div className="space-y-3">
          {analytics.stage_distribution && Object.entries(analytics.stage_distribution).map(([stage, count]) => (
            <div key={stage} className="flex items-center justify-between">
              <span className="text-gray-300">{stage}</span>
              <div className="flex items-center gap-3">
                <div className="w-48 bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full"
                    style={{ width: `${(count / (analytics.total_candidates || 1)) * 100}%` }}
                  ></div>
                </div>
                <span className="text-white font-semibold w-8 text-right">{count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Top Performing Jobs</h3>
          <div className="space-y-3">
            {analytics.top_jobs && analytics.top_jobs.map((job, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                <div>
                  <p className="text-white font-semibold">{job.title}</p>
                  <p className="text-xs text-gray-400">{job.department}</p>
                </div>
                <span className="text-cyan-400 font-bold">{job.candidates_count} candidates</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Hiring Metrics</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-purple-400" />
                <span className="text-white">Avg. Time to Hire</span>
              </div>
              <span className="text-purple-400 font-bold">{analytics.avg_time_to_hire || 0} days</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-green-400" />
                <span className="text-white">Success Rate</span>
              </div>
              <span className="text-green-400 font-bold">{analytics.success_rate || 0}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
